/* eslint-disable max-len */
import { ExportOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card, Col, DatePicker, message, Row, Select, Space, Spin, Typography,
} from 'antd';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';

import { useAppSelector } from 'app/store';
import { DropdownProgram } from 'common/components/DropdownProgram';
import { DropdownElement } from 'common/components/DropdownType';
import {
  exportAnnualReportService,
  exportAttendanceRecordService, exportMonthlyReportService, exportOutOfMainService, exportPayrollSummaryService, exportSalaryRecordService, exportTransferInMainService
} from 'common/services/extends/exports';
import { ExportAnnualRequestTypes, ExportAttendanceRecordRequestTypes } from 'common/services/extends/exports/types';
import { getAllPayrollPeriodsService } from 'common/services/extends/payrollPeriods';
import { exportTypeOptions } from 'common/utils/constant';
import { detectError } from 'common/utils/functions';
import { exportSalarySchema } from 'common/utils/schemas';
import roles, { getPermission } from 'configs/roles';

interface FormTypes {
  programCode?: number;
  payrollPeriodId?: number;
  month?: string;
  year?: string;
  exportType: number;
}

interface ExportSalarySummaryProps {
  queryKeyTable: (string | number | undefined)[];
}

const ExportSalarySummary: React.FC<ExportSalarySummaryProps> = ({ queryKeyTable }) => {
  const rolesUser = useAppSelector((state) => state.auth.roles);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const method = useForm<FormTypes>({
    defaultValues: {
      exportType: exportTypeOptions[0].value,
    },
    mode: 'onChange',
    resolver: yupResolver(exportSalarySchema),
  });

  const projectId = method.watch('programCode');
  const queryKey = ['payroll-periods-export', projectId];

  const handleReset = () => {
    method.reset();
    queryClient.invalidateQueries(queryKeyTable);
  };

  const {
    data: payrollData,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useInfiniteQuery(
    queryKey,
    ({ pageParam = 1 }) => getAllPayrollPeriodsService({
      page: projectId ? undefined : pageParam,
      _getCodeDetail: true,
      ...(projectId ? { _projectId: String(projectId) } : undefined),
    }),
    {
      getNextPageParam: (lastPage) => (lastPage.meta.page + 1 > lastPage.meta.totalPages ? undefined : lastPage.meta.page + 1),
      enabled: getPermission(rolesUser, roles.PAYROLL_PERIOD_INDEX),
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          message.error(t(detectError(status)));
        }
      }
    },
  );

  const exportTypeOptionsPermission = useMemo(() => {
    const anualReport = getPermission(rolesUser, roles.ANNUAL_REPORT);
    const attendanceRecord = getPermission(rolesUser, roles.ATTENDANCE_RECORD);
    const monthlyReport = getPermission(rolesUser, roles.MONTHLY_REPORT);
    const payrollSummary = getPermission(rolesUser, roles.PAYROLL_SUMMARY);
    const salaryRecord = getPermission(rolesUser, roles.SALARY_RECORD);
    const transferOutOfMain = getPermission(rolesUser, roles.TRANSFER_OUT_MAIN);
    const transferInMain = getPermission(rolesUser, roles.TRANSFER_IN_MAIN);
    const results = [];
    if (attendanceRecord) {
      results.push(exportTypeOptions[0]);
    }
    if (salaryRecord) {
      results.push(exportTypeOptions[1]);
    }
    if (transferOutOfMain) {
      results.push(exportTypeOptions[2]);
    }
    if (transferInMain) {
      results.push(exportTypeOptions[3]);
    }
    if (monthlyReport) {
      results.push(exportTypeOptions[4]);
    }
    if (anualReport) {
      results.push(exportTypeOptions[5]);
    }
    if (payrollSummary) {
      results.push(exportTypeOptions[6]);
    }
    return results;
  }, [rolesUser]);

  const { mutate: exportAttendanceRecordMutate, isLoading: exportAttendanceRecordLoading } = useMutation(
    ['exportAttendanceRecordMutate'],
    async (params: ExportAttendanceRecordRequestTypes) => exportAttendanceRecordService({ payrollPeriodId: params.payrollPeriodId }),
    {
      onSuccess: () => {
        message.success(t('message.exportSuccess'));
        handleReset();
      },
      onError: () => {
        message.error(t('message.exportError'));
      }
    }
  );

  const { mutate: exportSalaryRecordMutate, isLoading: exportSalaryRecordLoading } = useMutation(
    ['exportSalaryRecordMutate'],
    async (params: ExportAttendanceRecordRequestTypes) => exportSalaryRecordService({ payrollPeriodId: params.payrollPeriodId }),
    {
      onSuccess: () => {
        message.success(t('message.exportSuccess'));
        handleReset();
      },
      onError: () => {
        message.error(t('message.exportError'));
      }
    }
  );

  const { mutate: exportOutOfMainMutate, isLoading: exportOutOfMainLoading } = useMutation(
    ['exportOutOfMainMutate'],
    async (params: ExportAttendanceRecordRequestTypes) => exportOutOfMainService({ payrollPeriodId: params.payrollPeriodId }),
    {
      onSuccess: () => {
        message.success(t('message.exportSuccess'));
        handleReset();
      },
      onError: () => {
        message.error(t('message.exportError'));
      }
    }
  );

  const { mutate: exportTransferInMainMutate, isLoading: exportTransferInMainLoading } = useMutation(
    ['exportTransferInMainMutate'],
    async (params: ExportAttendanceRecordRequestTypes) => exportTransferInMainService({ payrollPeriodId: params.payrollPeriodId }),
    {
      onSuccess: () => {
        message.success(t('message.exportSuccess'));
        handleReset();
      },
      onError: () => {
        message.error(t('message.exportError'));
      }
    }
  );

  const { mutate: exportAnnualReportMutate, isLoading: exportAnnualReportLoading } = useMutation(
    ['exportAnnualReportMutate'],
    async (params: ExportAnnualRequestTypes) => exportAnnualReportService({
      year: params.year
    }),
    {
      onSuccess: () => {
        message.success(t('message.exportSuccess'));
        handleReset();
      },
      onError: () => {
        message.error(t('message.exportError'));
      }
    }
  );

  const { mutate: exportPayrollSummaryMutate, isLoading: exportPayrollSummaryLoading } = useMutation(
    ['exportPayrollSummaryMutate'],
    async (params: { year: string }) => exportPayrollSummaryService({
      year: params.year
    }),
    {
      onSuccess: () => {
        message.success(t('message.exportSuccess'));
        handleReset();
      },
      onError: () => {
        message.error(t('message.exportError'));
      }
    }
  );

  const { mutate: exportMonthlyReportMutate, isLoading: exportMonthlyReportLoading } = useMutation(
    ['exportMonthlyReportMutate'],
    async (params: ExportAnnualRequestTypes) => exportMonthlyReportService({
      year: params.year,
      month: params.month,
    }),
    {
      onSuccess: () => {
        message.success(t('message.exportSuccess'));
        handleReset();
      },
      onError: () => {
        message.error(t('message.exportError'));
      }
    }
  );

  const handleSubmit = async () => {
    const isValid = await method.trigger();
    if (!isValid) return;

    const {
      exportType, year, payrollPeriodId, month
    } = method.getValues();

    const yearSubmit = dayjs(year).format('YYYY');

    const date = dayjs(month).format('MM-YYYY').split('-');

    const monthData = date[0];
    const yearData = date[1];

    switch (exportType) {
      case exportTypeOptions[0].value:
        exportAttendanceRecordMutate({ payrollPeriodId });
        break;
      case exportTypeOptions[1].value:
        exportSalaryRecordMutate({ payrollPeriodId });
        break;
      case exportTypeOptions[2].value:
        exportOutOfMainMutate({ payrollPeriodId });
        break;
      case exportTypeOptions[3].value:
        exportTransferInMainMutate({ payrollPeriodId });
        break;
      case exportTypeOptions[4].value:
        exportMonthlyReportMutate({ month: monthData, year: yearData });
        break;
      case exportTypeOptions[5].value:
        exportAnnualReportMutate({ year: yearSubmit });
        break;
      case exportTypeOptions[6].value:
        exportPayrollSummaryMutate({ year: yearSubmit });
        break;
      default:
        break;
    }
  };

  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 10 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const formarttedData = useMemo(() => {
    if (!payrollData) return [];
    return payrollData?.pages.flatMap((page) => page.data.map((item) => ({
      label: item.payrollPeriodData?.codeDetail,
      value: item.payrollPeriodData?.id,
    })));
  }, [payrollData]);

  const exportTypeData = method.watch('exportType');

  const isFilterByProgram = useMemo(() => exportTypeData === 1 || exportTypeData === 2 || exportTypeData === 3 || exportTypeData === 4, [exportTypeData]);

  const isFilterByYear = useMemo(() => exportTypeData === 6 || exportTypeData === 7, [exportTypeData]);
  const isFilterByMonth = useMemo(() => exportTypeData === 5, [exportTypeData]);

  return (
    <FormProvider {...method}>
      <Row gutter={16}>
        <Col>
          <Space direction="vertical" size={24} style={{ width: '100%' }}>
            <Card title={<Typography.Title level={4}>{t('exportReports.salarySummary')}</Typography.Title>}>
              <div className="site-card-border-less-wrapper">
                <Space
                  direction="vertical"
                  size={12}
                  style={{ width: '100%' }}
                >
                  <Row gutter={16}>
                    <Col sm={8} xl={isFilterByProgram ? 8 : 12} className="u-mb-8">
                      <Typography.Text strong>
                        {t('exportReports.exportType')}
                        {' '}
                      </Typography.Text>
                      <Typography.Text strong type="danger">
                        *
                      </Typography.Text>
                      <Controller
                        name="exportType"
                        control={method.control}
                        render={({
                          field: { value, onChange },
                        }) => (
                          <DropdownElement
                            options={exportTypeOptionsPermission}
                            placeholder={t('exportReports.exportType')}
                            locale="vi"
                            value={value}
                            onChange={(val) => {
                              onChange(val);
                              method.setValue('payrollPeriodId', undefined);
                              method.setValue('programCode', undefined);
                            }}
                            size="large"
                            allowClear={false}
                          />
                        )}
                      />
                    </Col>
                    {
                      isFilterByProgram && (
                        <>
                          <Col sm={8} xl={8} className="u-mb-8">
                            <Typography.Text strong>
                              {t('timesheet.codeProgram')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="programCode"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <DropdownProgram
                                  placeholder={t('timesheet.codeProgram')}
                                  value={value}
                                  disabled={method.watch('exportType') === exportTypeOptions[6].value}
                                  onChange={(val) => {
                                    onChange(val);
                                    method.setValue('payrollPeriodId', undefined);
                                  }}
                                  error={error?.message}
                                  size="large"
                                />
                              )}
                            />
                          </Col>
                          <Col sm={8} xl={8} className="u-mb-8">
                            <Typography.Text strong>
                              {t('approveSalary.detailCode')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="payrollPeriodId"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <>
                                  <Select
                                    className="u-mt-8"
                                    onPopupScroll={handlePopupScroll}
                                    style={{ width: '100%' }}
                                    placeholder={t('approveSalary.detailCode')}
                                    size="large"
                                    disabled={method.watch('exportType') === exportTypeOptions[6].value}
                                    value={value}
                                    onChange={onChange}
                                  >
                                    {formarttedData.map((item) => (
                                      <Select.Option key={item.value} value={item.value}>
                                        {item.label}
                                      </Select.Option>
                                    ))}
                                    {isFetchingNextPage && (
                                      <Select.Option
                                        disabled
                                        key="loading"
                                      >
                                        <Spin size="small" />
                                      </Select.Option>
                                    )}
                                  </Select>
                                  {error?.message && (
                                    <div className="a-input_errorMessage">
                                      {error?.message}
                                    </div>
                                  )}
                                </>
                              )}
                            />
                          </Col>
                        </>
                      )
                    }
                    {
                      isFilterByMonth && (
                        <Col sm={12} xl={12}>
                          <Typography.Text strong>
                            {t('system.month')}
                            {' '}
                          </Typography.Text>
                          <Typography.Text strong type="danger">
                            *
                          </Typography.Text>
                          <Controller
                            name="month"
                            control={method.control}
                            render={({
                              field: { value, onChange },
                              fieldState: { error },
                            }) => (
                              <>
                                <DatePicker
                                  value={value ? dayjs(value) : undefined}
                                  format="MM-YYYY"
                                  onChange={onChange}
                                  style={{ width: '100%', height: '40px' }}
                                  size="large"
                                  className="u-mt-8"
                                  picker="month"
                                />
                                {error?.message && (
                                  <div className="a-input_errorMessage">
                                    {error?.message}
                                  </div>
                                )}
                              </>
                            )}
                          />
                        </Col>
                      )
                    }
                    {
                      isFilterByYear && (
                        <Col sm={12} xl={12}>
                          <Typography.Text strong>
                            {t('system.year')}
                            {' '}
                          </Typography.Text>
                          <Typography.Text strong type="danger">
                            *
                          </Typography.Text>
                          <Controller
                            name="year"
                            control={method.control}
                            render={({
                              field: { value, onChange },
                              fieldState: { error },
                            }) => (
                              <>
                                <DatePicker
                                  value={value ? dayjs(value) : undefined}
                                  format="YYYY"
                                  onChange={onChange}
                                  style={{ width: '100%', height: '40px' }}
                                  size="large"
                                  className="u-mt-8"
                                  picker="year"
                                />
                                {error?.message && (
                                  <div className="a-input_errorMessage">
                                    {error?.message}
                                  </div>
                                )}
                              </>
                            )}
                          />
                        </Col>
                      )
                    }
                  </Row>
                  <Row gutter={16}>
                    <Col sm={12} xl={24} className="u-mt-8" style={{ display: 'flex', justifyContent: 'end' }}>
                      <Button
                        size="middle"
                        type="primary"
                        icon={(<ExportOutlined />)}
                        onClick={handleSubmit}
                        disabled={exportAttendanceRecordLoading
                          || exportSalaryRecordLoading
                          || exportOutOfMainLoading
                          || exportAnnualReportLoading
                          || exportTransferInMainLoading
                          || exportPayrollSummaryLoading
                          || exportMonthlyReportLoading
                          || isLoading}
                      >
                        {t('exportReports.export')}
                      </Button>
                    </Col>
                  </Row>
                </Space>
              </div>
            </Card>
          </Space>
        </Col>
      </Row>
    </FormProvider>
  );
};

export default ExportSalarySummary;
